import React, { useRef, useEffect } from 'react';
import { VOTE_VALUES } from '@/utils/constants';
import type { VoteValue } from '@/types/index';

interface PointsGridProps {
  onVote: (value: VoteValue) => void;
  selectedValue?: VoteValue;
  disabled?: boolean;
}

export function PointsGrid({
  onVote,
  selectedValue,
  disabled = false,
}: PointsGridProps) {
  const gridRef = useRef<HTMLDivElement>(null);
  const selectedIndex = selectedValue
    ? VOTE_VALUES.indexOf(selectedValue)
    : -1;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (disabled || !gridRef.current) return;

      const buttons = gridRef.current.querySelectorAll('button');
      const currentIndex = Array.from(buttons).findIndex((btn) =>
        btn.matches(':focus'),
      );

      let nextIndex = currentIndex;

      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault();
          nextIndex = currentIndex < buttons.length - 1 ? currentIndex + 1 : 0;
          break;
        case 'ArrowLeft':
          e.preventDefault();
          nextIndex = currentIndex > 0 ? currentIndex - 1 : buttons.length - 1;
          break;
        case 'ArrowDown':
          e.preventDefault();
          // Move to next row (assuming 6 columns on large screens)
          const cols = window.innerWidth >= 1024 ? 6 : window.innerWidth >= 768 ? 4 : 3;
          nextIndex = Math.min(currentIndex + cols, buttons.length - 1);
          break;
        case 'ArrowUp':
          e.preventDefault();
          const colsUp = window.innerWidth >= 1024 ? 6 : window.innerWidth >= 768 ? 4 : 3;
          nextIndex = Math.max(currentIndex - colsUp, 0);
          break;
        case 'Enter':
        case ' ':
          if (currentIndex >= 0) {
            e.preventDefault();
            onVote(VOTE_VALUES[currentIndex]);
          }
          return;
        default:
          return;
      }

      if (nextIndex >= 0 && nextIndex < buttons.length) {
        buttons[nextIndex].focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [disabled, onVote]);

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLButtonElement>,
    value: VoteValue,
  ) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!disabled) {
        onVote(value);
      }
    }
  };

  return (
    <div
      ref={gridRef}
      className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3"
      role="group"
      aria-label="Voting options"
    >
      {VOTE_VALUES.map((value: VoteValue) => {
        const isSelected = selectedValue === value;
        return (
          <button
            key={value}
            type="button"
            onClick={() => !disabled && onVote(value)}
            onKeyDown={(e) => handleKeyDown(e, value)}
            disabled={disabled}
            aria-pressed={isSelected}
            aria-label={`Vote ${value === '?' ? 'question mark' : value} points`}
            tabIndex={disabled ? -1 : 0}
            className={`
              px-4 py-3 rounded-lg font-semibold text-lg transition-all
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
              ${
                isSelected
                  ? 'bg-blue-600 text-white shadow-lg scale-105'
                  : 'bg-white text-gray-800 border-2 border-gray-300 hover:border-blue-500 hover:shadow-md'
              }
              ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
          >
            {value === '?' ? '?' : value}
          </button>
        );
      })}
    </div>
  );
}

